/**
 * Money — web mirror of `mobile/components/Money.tsx`.
 *
 * Currency-aware amount with tabular numerics + optional unit, prop surface
 * matched to the mobile component (amount / unit / currency / size / color /
 * weight / strike). Per `preview/type-money.html` all numbers use
 * `font-variant-numeric: tabular-nums` so columns of money align across rows;
 * the FSE content rule is to never render a bare number — always currency
 * (ZAR "R") plus unit.
 *
 *   <Money amount={1250} />               → "R 1,250"
 *   <Money amount={580} unit="/ bag" />   → "R 580 / bag"
 *   <Money amount={12500} size="xl" />    → big bold "R 12,500"
 */
import { Box } from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, typography } from '../theme/tokens.js';

export type MoneySize = 'sm' | 'md' | 'lg' | 'xl' | 'display';

export interface MoneyProps {
  /** Amount to display. Formatted with thousands separators. */
  amount: number;
  /** Unit suffix, e.g. "kg", "bag", "/ bag". Optional. */
  unit?: string;
  /** Currency prefix. Default "R" (ZAR). */
  currency?: string;
  /** Size scale. Default "md". */
  size?: MoneySize;
  /** Override colour (defaults to the kit ink colour). */
  color?: string;
  /** Override font weight. Default 700 (bold). */
  weight?: number;
  /** Apply a strikethrough (e.g. crossed-out price). */
  strike?: boolean;
  /** Pass-through className for layout composition. */
  className?: string;
}

const SIZE_TO_FONT: Record<MoneySize, number> = {
  sm: typography.bodyMedium.fontSize, // 16
  md: typography.titleLarge.fontSize, // 22
  lg: typography.headlineSmall.fontSize, // 24
  xl: typography.displaySmall.fontSize, // 36
  display: typography.displayLarge.fontSize, // 57
};

export const Money = forwardRef<HTMLSpanElement, MoneyProps>(function Money(
  { amount, unit, currency = 'R', size = 'md', color, weight = 700, strike, className },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const formatted = new Intl.NumberFormat('en-ZA').format(amount);
  return (
    <Box
      component="span"
      ref={ref}
      className={className}
      sx={{
        fontFamily: typography.fontFamily,
        fontSize: SIZE_TO_FONT[size],
        fontWeight: weight,
        color: color ?? sys.onSurface,
        fontVariantNumeric: 'tabular-nums',
        textDecorationLine: strike ? 'line-through' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {currency} {formatted}
      {unit ? ` ${unit}` : ''}
    </Box>
  );
});
